import { Component } from 'react';
import { finalize, debounceTime } from 'rxjs/operators';

import { Commons } from '../../../commons/commons';
import { BasicValidators, FormControl, FormGroup } from '../../../models/forms';
import { Logger } from '../../../models/logger';
import { LoginService } from '../../../services/login.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import ReCaptcha from '../recaptcha/recaptcha.component';
import { TabBody, TabHeader, TabsComponent } from '../tabs/tabs.component';
import Link from 'next/link';
import { RxUtils } from '../../../utils/rx-utils';
import { Subscription } from 'rxjs';

import styles from './login.component.module.scss';

export type LoginDialogMode = 'login' | 'register' | 'recover-password';

export interface LoginComponentProps {
    children?: React.ReactNode;
    registerEnabled: boolean;
    passwordResetEnabled: boolean;
    mode: LoginDialogMode;
    submitBtnText: string;
    onSuccess: (data: string) => void,
}

export interface LoginComponentState {
    mode: LoginDialogMode;
    captchaKey: number;
}

export default class LoginComponent extends Component<LoginComponentProps, LoginComponentState> {
    private readonly logger = Logger.getLogger('LoginComponent');

    formGroup: FormGroup;
    formGroupSubcription: Subscription;
    isBrowser = false;

    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(SpinnerService) private spinnerService: SpinnerService;
    @lazyInject(UIContext) private uiContext: UIContext;

    readonly state: Readonly<LoginComponentState> = {
        mode: this.props?.mode ?? 'login',
        captchaKey: 0,
    };
    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.updateModal(this.state.mode);
    }

    // TODO Preact not fixed
    // ngOnChanges(changes: SimpleChanges): void {
    //     if (changes.mode.currentValue) {
    //         this.currentMode = changes.mode.currentValue;
    //         this.updateModal();
    //     }
    // }

    async login() {
        const instance = await this.spinnerService.show();
        this.loginService.login(this.formGroup.value.email, this.formGroup.value.password, this.formGroup.value.token)
            .pipe(
                finalize(() => {
                    this.spinnerService.hide(instance);
                }),
            ).subscribe(
                data => {
                    this.resetCaptcha();
                    if (data && data.error) {
                        this.uiContext.showErrorDialog({ title: 'Error', text: 'Login failed. Please check your credentials.' });
                    } else {
                        this.uiContext.successToast('Success!');
                        this.props.onSuccess(this.state.mode);
                    }
                },
                err => {
                    this.resetCaptcha();
                    this.logger.info(err);
                    this.uiContext.handleError(err);
                },
            );
    }

    register() {
        this.spinnerService.runWithLoader(
            async () => {
                try {
                    // Backend returns a session bearer on successful registration, so we do
                    // NOT chain a second /auth/login — that would reuse the consumed captcha.
                    await this.loginService.register(
                        this.formGroup.value.email,
                        this.formGroup.value.password,
                        this.formGroup.value.token,
                    ).toPromise();
                    this.props.onSuccess(this.state.mode);

                } catch (err) {
                    this.logger.info(err);
                    this.uiContext.handleError(err);
                } finally {
                    this.resetCaptcha();
                }

            },
            'Creating account...'
        ).catch(err => {
            console.log(err);
        });
    }

    async recover() {
        const instance = await this.spinnerService.show();
        this.loginService.recover(this.formGroup.value.email, this.formGroup.value.token)
            .pipe(
                finalize(() => {
                    this.spinnerService.hide(instance);
                }),
            ).subscribe(
                data => {
                    this.resetCaptcha();
                    this.uiContext.successToast('Success', 'We sent password reset instructions to your email.');
                    this.props.onSuccess(this.state.mode);
                },
                err => {
                    this.resetCaptcha();
                    this.logger.info(err);
                    this.uiContext.handleError(err);
                },
            );
    }

    updateModal(tabname) {
        RxUtils.safeCloseSubscriber(this.formGroupSubcription);
        if (tabname === 'login') {
            this.formGroup = new FormGroup({
                email: new FormControl('', { required: BasicValidators.required }), // Validators.email, 
                password: new FormControl('', { required: BasicValidators.required }),
                token: new FormControl(undefined, { required: BasicValidators.required }),
            });

        } else if (tabname === 'register') {
            this.formGroup = new FormGroup({
                email: new FormControl('', { required: BasicValidators.required }),
                password: new FormControl('', { required: BasicValidators.required }),
                repassword: new FormControl('', { required: BasicValidators.required }),
                token: new FormControl(undefined, { required: BasicValidators.required }),
                tosAccepted: new FormControl(false, { required: BasicValidators.required, requiredTruthy: BasicValidators.requiredTruthy }),
            });
        } else if (tabname === 'recover-password') {
            this.formGroup = new FormGroup({
                email: new FormControl('', { required: BasicValidators.required }),
                token: new FormControl(undefined, { required: BasicValidators.required }),
            });
        } else {
            throw new Error('Login state is not supported');
        }

        this.formGroupSubcription = this.formGroup.valueChanges.pipe(
            debounceTime(300),
        ).subscribe({
            next: () => {
                setTimeout(() => this.forceUpdate(), 100);
            }
        });

        if (Commons.DEBUG) {
            // TODO Preact not supported yet
            // this.formGroup.get('token').disable();
        }
    }

    handleCorrectCaptcha(event) {
        this.formGroup.get('token').setValue(event);
    }

    resetCaptcha() {
        this.formGroup.get('token').setValue(undefined);
        this.setState({ captchaKey: this.state.captchaKey + 1 });
    }

    tabChanged(mode) {
        this.setState({
            mode: mode,
            captchaKey: this.state.captchaKey + 1,
        });
        this.updateModal(mode);
        this.forceUpdate();
    }

    setFromControlValue(control, value) {
        control.setValue(value);
    }



    getTOSAcceptFormControl() {
        return <div className="mt-3 mb-3" >
            <div className="custom-control custom-checkbox">
                <input type="checkbox" className="custom-control-input" id="customCheck1"
                    onChange={(e: any) => this.formGroup.controls['tosAccepted'].setValue(e.target.checked)}
                />
                <label className={`custom-control-label ${styles['tos-label']}`} htmlFor="customCheck1">
                    I agree to the <Link href="/terms-of-service" prefetch={false} target="_blank">
                        Terms of Service
                    </Link>
                </label>
            </div>
        </div>
    }

    isValid() {
        return this.formGroup?.valid;
    }

    // A real <form> (not a div): Chrome warns on password fields outside forms,
    // password managers key their save/autofill heuristics on it, and it gives
    // Enter-to-submit for free. Handlers live on onSubmit ONLY — a submit button
    // with its own onClick would run the handler twice per click. Same pattern on
    // all three tabs.
    getHTMLForLoginTab() {
        return <form className="mt-3 mb-3" method="post"
            onSubmit={e => { e.preventDefault(); this.login(); }}>
            <div className={styles['form-group']} >
                <label className={styles['input-label']}>Email address</label>
                <input type="email" name="email" autoComplete="email" className={styles['input-field']} aria-describedby="emailHelp" placeholder="e.g. user@example.com"
                    onInput={(e: any) => this.setFromControlValue(this.formGroup.get('email'), e.target.value)
                    } />
            </div >
            <div className={styles['form-group']} >
                <label className={styles['input-label']}>Password</label>
                <input type="password" name="password" autoComplete="current-password" className={styles['input-field']} placeholder="••••••••"
                    onInput={(e: any) => this.setFromControlValue(this.formGroup.get('password'), e.target.value)} />
            </div >
            <div className="mt-3 mb-3" >
                <ReCaptcha key={this.state.captchaKey} onChange={e => this.handleCorrectCaptcha(e)}></ReCaptcha>
            </div >
            <button type="submit" className={styles['submit-btn']}
            > {this.props.submitBtnText}</button >
        </form >;
    }


    getHTMLForRegisterTab() {
        return <form className="mt-3 mb-3" method="post"
            onSubmit={e => { e.preventDefault(); if (this.isValid()) this.register(); }}>
            <div className={styles['form-group']} >
                <label className={styles['input-label']}>Email address</label>
                <input type="email" name="email" autoComplete="email" className={styles['input-field']} aria-describedby="emailHelp" placeholder="e.g. user@example.com"
                    onInput={(e: any) => this.setFromControlValue(this.formGroup.get('email'), e.target.value)
                    } />
            </div >
            <div className={styles['form-group']} >
                <label className={styles['input-label']} htmlFor="exampleInputPassword1">Password</label>
                <input type="password" name="new-password" autoComplete="new-password" className={styles['input-field']} id="exampleInputPassword1" placeholder="Minimum 6 characters"
                    onInput={(e: any) => this.setFromControlValue(this.formGroup.get('password'), e.target.value)} />
            </div >
            <div className={styles['form-group']} >
                <label className={styles['input-label']}>Re-type Password</label>
                <input type="password" name="confirm-password" autoComplete="new-password" className={styles['input-field']} placeholder="Confirm password"
                    onInput={(e: any) => this.setFromControlValue(this.formGroup.get('repassword'), e.target.value)} />
            </div >
            <div className="mt-3 mb-3" >
                <label className={styles['input-label']}>Verification</label>
                <ReCaptcha key={this.state.captchaKey} onChange={e => this.handleCorrectCaptcha(e)}></ReCaptcha>
            </div>
            {
                this.getTOSAcceptFormControl()
            }
            <button type="submit" className={styles['submit-btn']} disabled={!this.isValid()}
            > {this.props.submitBtnText}</button >
        </form >;
    }

    getHTMLForPasswordReset() {
        return <form className="mt-3 mb-3" method="post"
            onSubmit={e => { e.preventDefault(); this.recover(); }}>
            <div className={styles['form-group']} >
                <label className={styles['input-label']}>Email address</label>
                <input type="email" name="email" autoComplete="email" className={styles['input-field']} aria-describedby="emailHelp" placeholder="e.g. user@example.com"
                    onInput={(e: any) => this.setFromControlValue(this.formGroup.get('email'), e.target.value)
                    } />
            </div >
            <div className="mt-3 mb-3" >
                <label className={styles['input-label']}>Verification</label>
                <ReCaptcha key={this.state.captchaKey} onChange={e => this.handleCorrectCaptcha(e)}></ReCaptcha>
            </div >
            <button type="submit" className={styles['submit-btn']}
            > {this.props.submitBtnText}</button >
        </form >;
    }

    render() {
        return <TabsComponent activeTabName={this.state.mode} onTabChange={(tabname) => this.tabChanged(tabname)}>
            <TabHeader type="header" headerFor="login">
                Login
            </TabHeader>
            <TabHeader type="header" headerFor="register">
                Register
            </TabHeader>
            <TabHeader type="header" headerFor="recover-password">
                Reset Password
            </TabHeader>
            <TabBody name="login" type="body">
                {
                    this.state.mode === 'login' && this.getHTMLForLoginTab()
                }
            </TabBody>
            <TabBody name="register" type="body">
                {
                    this.state.mode === 'register' && this.getHTMLForRegisterTab()
                }
            </TabBody>
            <TabBody name="recover-password" type="body">
                {
                    this.state.mode === 'recover-password' && this.getHTMLForPasswordReset()
                }
            </TabBody>
        </TabsComponent>
    }
}
